import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import ImageUpload from "@/components/admin/ImageUpload";
import { useSettingsEditor, SaveBar } from "@/components/admin/useSettingsEditor";

const ICON_OPTIONS = [
  "ShieldCheck", "Award", "HeartPulse", "Sparkles", "Star", "Trophy", "BookOpen",
  "GraduationCap", "Stethoscope", "FlaskConical",
];

export default function AdminHero() {
  const { draft, patchPath, save, reset, saving } = useSettingsEditor();
  if (!draft) return null;
  const h = draft.hero || {};
  const images = h.images || [];
  const badges = h.trustBadges || [];
  const sp = h.socialProof || { label: "", caption: "", images: [] };
  const spImages = sp.images || [];

  const setImage = (idx, url) => {
    const next = [...images];
    next[idx] = url;
    patchPath("hero.images", next);
  };
  const addImage = () => patchPath("hero.images", [...images, ""]);
  const removeImage = (idx) => patchPath("hero.images", images.filter((_, i) => i !== idx));

  const setSpImage = (idx, url) => {
    const next = [...spImages];
    // Ensure 4 slots exist
    while (next.length < 4) next.push("");
    next[idx] = url;
    patchPath("hero.socialProof.images", next.slice(0, 4));
  };

  const setBadge = (idx, key, v) => {
    const next = [...badges];
    next[idx] = { ...next[idx], [key]: v };
    patchPath("hero.trustBadges", next);
  };
  const addBadge = () => patchPath("hero.trustBadges", [...badges, { label: "New Badge", icon: "ShieldCheck" }]);
  const removeBadge = (idx) => patchPath("hero.trustBadges", badges.filter((_, i) => i !== idx));

  return (
    <div data-testid="cms-hero-page">
      <div className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] brand-secondary-text mb-2">Home Page</p>
        <h1 className="font-heading text-3xl sm:text-4xl text-slate-900">Hero Section</h1>
        <p className="text-slate-500 mt-2">Edit the main headline, call-to-actions and hero images.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-6 sm:p-8 space-y-8">
        {/* Copy */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="sm:col-span-2">
            <Label className="text-slate-700 text-sm">Eyebrow (small tag above headline)</Label>
            <Input value={h.eyebrow || ""} onChange={(e) => patchPath("hero.eyebrow", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" data-testid="hero-eyebrow" />
          </div>
          <div>
            <Label className="text-slate-700 text-sm">Title — Leading</Label>
            <Input value={h.titleLeading || ""} onChange={(e) => patchPath("hero.titleLeading", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
          </div>
          <div>
            <Label className="text-slate-700 text-sm">Title — Highlight (primary color)</Label>
            <Input value={h.titleAccentPrimary || ""} onChange={(e) => patchPath("hero.titleAccentPrimary", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
          </div>
          <div>
            <Label className="text-slate-700 text-sm">Title — Middle</Label>
            <Input value={h.titleMiddle || ""} onChange={(e) => patchPath("hero.titleMiddle", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
          </div>
          <div>
            <Label className="text-slate-700 text-sm">Title — Highlight (secondary color)</Label>
            <Input value={h.titleAccentSecondary || ""} onChange={(e) => patchPath("hero.titleAccentSecondary", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
          </div>
          <div>
            <Label className="text-slate-700 text-sm">Title — Trailing</Label>
            <Input value={h.titleTrailing || ""} onChange={(e) => patchPath("hero.titleTrailing", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-slate-700 text-sm">Subtitle</Label>
            <Textarea value={h.subtitle || ""} onChange={(e) => patchPath("hero.subtitle", e.target.value)} className="mt-1.5 rounded-xl border-slate-200 min-h-[100px]" data-testid="hero-subtitle" />
          </div>

          <div>
            <Label className="text-slate-700 text-sm">Primary CTA — Text</Label>
            <Input value={h.ctaPrimaryText || ""} onChange={(e) => patchPath("hero.ctaPrimaryText", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
          </div>
          <div>
            <Label className="text-slate-700 text-sm">Primary CTA — Link</Label>
            <Input value={h.ctaPrimaryLink || ""} onChange={(e) => patchPath("hero.ctaPrimaryLink", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
          </div>
          <div>
            <Label className="text-slate-700 text-sm">Secondary CTA — Text</Label>
            <Input value={h.ctaSecondaryText || ""} onChange={(e) => patchPath("hero.ctaSecondaryText", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
          </div>
          <div>
            <Label className="text-slate-700 text-sm">Secondary CTA — Link</Label>
            <Input value={h.ctaSecondaryLink || ""} onChange={(e) => patchPath("hero.ctaSecondaryLink", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
          </div>

          <div>
            <Label className="text-slate-700 text-sm">Info Badge Title (right tile)</Label>
            <Input value={h.badgeTitle || ""} onChange={(e) => patchPath("hero.badgeTitle", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
          </div>
          <div>
            <Label className="text-slate-700 text-sm">Info Badge Text</Label>
            <Input value={h.badgeText || ""} onChange={(e) => patchPath("hero.badgeText", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-slate-700 text-sm">Info Badge Sub-text</Label>
            <Input value={h.badgeSubtext || ""} onChange={(e) => patchPath("hero.badgeSubtext", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
          </div>
        </div>

        {/* Trust Badges */}
        <div className="border-t border-slate-100 pt-6">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Trust Badges</p>
            <Button variant="outline" size="sm" onClick={addBadge} className="rounded-full h-9" data-testid="add-badge">
              <Plus className="w-4 h-4 mr-1.5" /> Add
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {badges.map((b, i) => (
              <div key={i} className="rounded-xl border border-slate-200 p-3 flex items-center gap-2">
                <select
                  value={b.icon || "ShieldCheck"}
                  onChange={(e) => setBadge(i, "icon", e.target.value)}
                  className="h-10 rounded-lg border border-slate-200 text-sm px-2 bg-white"
                >
                  {ICON_OPTIONS.map((op) => (<option key={op} value={op}>{op}</option>))}
                </select>
                <Input value={b.label || ""} onChange={(e) => setBadge(i, "label", e.target.value)} className="h-10 rounded-lg border-slate-200" />
                <button className="text-rose-500 p-2" onClick={() => removeBadge(i)} aria-label="Remove">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Images */}
        <div className="border-t border-slate-100 pt-6">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Hero Images</p>
            {images.length < 4 && (
              <Button variant="outline" size="sm" onClick={addImage} className="rounded-full h-9" data-testid="add-hero-image">
                <Plus className="w-4 h-4 mr-1.5" /> Add
              </Button>
            )}
          </div>
          <p className="text-xs text-slate-500 mb-4">
            Upload 1&ndash;4 photos. The hero grid adapts automatically: <b>1</b> image = full tile, <b>2</b> = side-by-side, <b>3</b> = three columns, <b>4</b> = 2&times;2 grid. Square photos (1:1) work best.
          </p>
          <div className="grid grid-cols-1 gap-4">
            {images.map((img, i) => (
              <div key={i} className="rounded-xl border border-slate-200 p-4 flex items-start gap-4">
                <div className="flex-1">
                  <ImageUpload
                    label={`Image ${i + 1}`}
                    value={img}
                    onChange={(url) => setImage(i, url)}
                    folder="images"
                    testid={`hero-image-${i}`}
                    height={120}
                  />
                </div>
                <button className="text-rose-500 p-2 mt-6" onClick={() => removeImage(i)} aria-label="Remove">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Social Proof — 4 avatar photos + label/caption */}
        <div className="border-t border-slate-100 pt-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">Social Proof (Avatar Cluster)</p>
          <p className="text-xs text-slate-500 mb-4">
            Shown below the hero CTAs. Upload <b>4 small photos</b> of students (circular, ~96&times;96). The text next to them is fully editable.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
            <div>
              <Label className="text-slate-700 text-sm">Label (bold text)</Label>
              <Input
                value={sp.label || ""}
                onChange={(e) => patchPath("hero.socialProof.label", e.target.value)}
                placeholder="20,000+ students"
                className="mt-1.5 h-11 rounded-xl border-slate-200"
                data-testid="social-proof-label"
              />
            </div>
            <div>
              <Label className="text-slate-700 text-sm">Caption (below the label)</Label>
              <Input
                value={sp.caption || ""}
                onChange={(e) => patchPath("hero.socialProof.caption", e.target.value)}
                placeholder="shaping careers with Ambigara"
                className="mt-1.5 h-11 rounded-xl border-slate-200"
                data-testid="social-proof-caption"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="rounded-xl border border-slate-200 p-4">
                <ImageUpload
                  label={`Avatar ${i + 1}`}
                  value={spImages[i] || ""}
                  onChange={(url) => setSpImage(i, url)}
                  folder="images"
                  testid={`social-proof-avatar-${i}`}
                  height={120}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <SaveBar onSave={save} onReset={reset} saving={saving} />
    </div>
  );
}
